import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { extendMoment } from 'moment-range';
import originalMoment from 'moment';
import append from 'ramda/src/append';
import concat from 'ramda/src/concat';
import equals from 'ramda/src/equals';
import findIndex from 'ramda/src/findIndex';
import filter from 'ramda/src/filter';
import isEmpty from 'ramda/src/isEmpty';
import keys from 'ramda/src/keys';
import length from 'ramda/src/length';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import remove from 'ramda/src/remove';
import toUpper from 'ramda/src/toUpper';
import toLower from 'ramda/src/toLower';
import uniqBy from 'ramda/src/uniqBy';
import AutoComplete from '@components/AutoComplete';
import Avatar from '@components/@core/Avatar';
import Button from '@components/@core/AnimatedButton';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import Icon from '@components/@core/Icon';
import Rating from '@components/@core/Rating';
import Pagination from '@components/@core/Pagination';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import VendorActions from '~/actions/vendor';
import withNavigate from '~/hocs/withNavigate';

const moment = extendMoment(originalMoment);

const VendorItem = ({ vendor, onOpen, onRate, lastItem }) => {
    const { name, parent, avatar, stats } = vendor;
    const rating_recommended_avg = stats?.rating_recommended_avg ?? 0;
    const rating_avg = stats?.rating_avg ?? 0;
    const { contact, description, sponsored_level } = vendor.data;
    const sponsoredLevel = sponsored_level ?? 'wiki';
    const logo = sponsoredLevel !== 'wiki' ? avatar : '/images/image-not-provided.jpg';
    const isSponsored = sponsoredLevel === 'gold' || sponsoredLevel === 'silver';

    return (
        <div className="vendor-company-item-wrapper px-4">
            <Row className={`vendor-company-item ssm-column mx-0 py-4 ${!lastItem && 'border-bottom'}`}>
                <Col xs={12} md={9} xl={10} className="cursor-pointer ps-md-0 px-3" onClick={onOpen}>
                    <div className="d-flex jc-between" itemProp="aggregateRating" itemScope itemType="http://schema.org/AggregateRating">
                        <Avatar className="company-avatar" initial="/images/default_company.svg" type="logo" size="extraLarge" url={logo} name={name} isSponsored={isSponsored} />
                        <div className="pl-4 right-content">
                            <p className="dsl-b18 text-400 mb-0" itemProp="itemReviewed" itemScope itemType="http://schema.org/Product">
                                <span itemProp="name">{name}</span>
                            </p>
                            <div className="d-flex ssm-column d-h-start mb-1 ai-center ssm-ai-start">
                                {stats && <p className="dsl-b14 mb-0 mt-2">{`${stats?.product_count} Products, ${stats?.rating_count ?? '0 '} Reviews`}</p>}
                            </div>
                            <div className="d-flex ssm-column d-h-start mb-3 ai-center ssm-ai-start">
                                {stats?.rating_avg ? (
                                    <>
                                        <div className="d-none" itemProp="ratingValue">
                                            {Number(rating_avg)}
                                        </div>
                                        <Rating className="mr-3" score={Number(rating_avg)} />
                                        <p className="dsl-b14 mb-0">
                                            <span itemProp="reviewCount">{Number(rating_recommended_avg).toFixed(0)}</span>
                                            <span>% Recommended</span>
                                        </p>
                                    </>
                                ) : (
                                    <Button className="p-0" name="Leave first rating" type="link" />
                                )}
                            </div>
                            <div className="dsl-b16 mb-3 truncate-two" dangerouslySetInnerHTML={{ __html: description }} />
                        </div>
                    </div>
                </Col>
                <Col xs={12} md={3} xl={2} className="contact-detail p-0">
                    <div className="d-flex flex-md-column ai-end w-100">
                        <p className="dsl-m14 mb-1">{sponsoredLevel === 'wiki' ? 'Phone not provided' : contact?.phone}</p>
                    </div>
                    <div className="d-flex flex-column ai-center">
                        <Button type="medium" name="Rate Vendor" onClick={onRate} />
                        {vendor.logo && contact && !isEmpty(contact.phone) && <Button type="medium" name="SPECIAL QUOTE" />}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

class ByCompany extends Component {
    listContainerRef = createRef();
    vendorAlphaListRef = createRef();
    state = {
        vendors: [],
        companies: null,
        vendorAlphaList: [],
        search: null,
        selected: null,
        current: 1,
        per: 15,
        dateStart: moment().subtract(2, 'years').format('YYYY-MM-DD'),
        dateEnd: moment().format('YYYY-MM-DD'),
        // To make sure A-K are listed
        sideBarMaxHeight: 620,
        vendorAlphaListScrollPosition: 0,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { vendorCompaniesAlphabet } = nextProps;
        const { companies } = prevState;
        if (vendorCompaniesAlphabet && !equals(vendorCompaniesAlphabet, companies)) {
            let vendors = [];
            let vendorAlphaList = keys(vendorCompaniesAlphabet).map((alphaKey, index) => {
                const items = vendorCompaniesAlphabet[alphaKey].map(({ id, name }) => ({
                    id,
                    name,
                    alphaKey,
                }));
                vendors = concat(vendors, items);
                return {
                    id: index,
                    name: toUpper(alphaKey),
                    count: vendorCompaniesAlphabet[alphaKey].length,
                };
            });
            const noOptionIdx = findIndex(propEq('name', 'NO'))(vendorAlphaList);
            if (noOptionIdx > -1) {
                const noOption = { ...vendorAlphaList[noOptionIdx] };
                vendorAlphaList = remove(noOptionIdx, 1, vendorAlphaList);
                vendorAlphaList = append(noOption, vendorAlphaList);
            }
            vendors = uniqBy(prop('id'), vendors);
            return {
                vendors,
                vendorAlphaList,
                companies: vendorCompaniesAlphabet,
                selected: vendorAlphaList[0],
            };
        }
        return null;
    }

    componentDidMount() {
        const { selected } = this.state;
        this.props.getCompanies({ sort: 'alphabet' });
        const query = new URLSearchParams(this.props.location?.search || '');
        const _name = query.get('name') || selected?.name;
        const _id = query.get('id') || selected?.id;
        const _scrollTo = query.get('scrollTo');
        if (_scrollTo) {
            this.vendorAlphaListRef.current.scrollTop = Number(_scrollTo);
            this.setState({
                vendorAlphaListScrollPosition: Number(_scrollTo),
            });
        }

        if (_name && _id) {
            this.setState({ selected: { name: _name, id: Number(_id) }, search: _name, current: 1, per: 15 });
            const payload = {
                search: _name !== 'NO' ? _name : null,
                dateStart: this.state.dateStart,
                dateEnd: this.state.dateEnd,
                verbatim: 1,
                page: 1,
                perPage: 15,
            };
            this.props.getCompanies({ payload });
        }
    }

    handleClickLetter = (selected) => () => {
        const { dateStart, dateEnd } = this.state;
        this.setState({ selected, search: selected.name !== 'NO' ? selected.name : null, current: 1, per: 15 });
        const payload = {
            search: selected.name !== 'NO' ? selected.name : null,
            dateStart,
            dateEnd,
            verbatim: 1,
            page: 1,
            perPage: 15,
        };

        this.props.navigate(`?name=${selected.name}&id=${selected.id}&scrollTo=${this.state.vendorAlphaListScrollPosition}`, { replace: true });
        this.props.getCompanies({ payload });
    };

    handleSearchVendor = (search) => {
        const { dateStart, dateEnd, selected } = this.state;
        this.setState({ search, current: 1, per: 15 });
        const payload = {
            search: search ?? (selected?.name !== 'NO' ? selected?.name : null),
            dateStart,
            dateEnd,
            verbatim: 1,
            page: 1,
            perPage: 15,
        };
        this.props.getCompanies({ payload });
    };

    handleOpenCompany = (vr) => () => {
        const payload = { id: vr?.business_id };
        const route = vr?.entity?.slug ? { pathname: `/${vr.entity.slug}/about` } : { pathname: '/business/all', search: `?id=${vr?.business_id}` };
        this.props.openCompanyView(payload, route);
    };

    handleOpenRate = (vr) => () => {
        const { authenticated, location } = this.props;
        if (authenticated) {
            const after = {
                type: 'MODAL_REQUEST',
                payload: {
                    type: 'Rate Vendor',
                    data: { before: { company: vr?.id, category: null, product: null } },
                },
            };
            this.props.getCompaniesInfo({ after });
            this.props.getCategories({ isCategory: true });
        } else {
            const route = location.pathname;
            this.props.toggleModal({ type: 'Authentication', data: { before: { route } } });
        }
    };

    handlePer = (per) => {
        const { current, dateStart, dateEnd, selected, search } = this.state;
        const payload = {
            search: search ?? (selected?.name !== 'NO' ? selected?.name : null),
            dateStart,
            dateEnd,
            verbatim: 1,
            page: per > 50 ? 1 : current,
            perPage: per > 50 ? 500 : per,
        };
        this.props.getCompanies({ payload });
        this.setState({ per });
        if (per > 50) this.setState({ current: 1 });
    };

    handlePagination = (e) => {
        const { dateStart, dateEnd, selected, per, search } = this.state;
        const payload = {
            search: search ?? (selected?.name !== 'NO' ? selected?.name : null),
            dateStart,
            dateEnd,
            verbatim: 1,
            page: e,
            perPage: per,
        };
        this.props.getCompanies({ payload });
        this.setState({ current: e });
    };

    handleDateChange = (e) => {
        const dateStart = e.start.format('YYYY-MM-DD');
        const dateEnd = e.end.format('YYYY-MM-DD');
        this.setState({ dateStart, dateEnd });
        this.handleSearchVendor(this.state.search);
    };

    getFeaturedList = () => {
        const { vendorCompanies } = this.props;
        return filter((x) => x.data?.sponsored_level === 'gold' || x.data?.sponsored_level === 'silver', vendorCompanies?.data || []);
    };

    getStandardList = () => {
        const { vendorCompanies } = this.props;
        return filter((x) => x.data?.sponsored_level !== 'gold' && x.data?.sponsored_level !== 'silver', vendorCompanies?.data || []);
    };

    handleVendorAlphaListScroll = () => {
        const scrollPosition = this.vendorAlphaListRef.current.scrollTop;
        this.setState({
            vendorAlphaListScrollPosition: scrollPosition,
        });
        const query = new URLSearchParams(this.props.location?.search || '');
        const _name = query.get('name');
        const _id = query.get('id');
        let queryString = '?';
        if (_name && _id) {
            queryString += `name=${_name}&id=${_id}&scrollTo=${scrollPosition}`;
        } else {
            queryString += `scrollTo=${scrollPosition}`;
        }

        this.props.navigate(queryString, { replace: true });
    };

    render() {
        const { vendorCompanies } = this.props;
        const { vendors, vendorAlphaList, search, selected, current, per, dateStart, dateEnd } = this.state;
        const featured = this.getFeaturedList();
        const standard = this.getStandardList();

        return (
            <ErrorBoundary className="vendor-rating-company">
                <Helmet>
                    <title>Dealership Vendor Ratings by company | DrivingSales</title>
                    <meta name="description" content="Dealership Vendor Ratings by company | DrivingSales" />
                </Helmet>
                <div className="d-h-start vendor-by-company mb-3 mt-2">
                    <Button className="btn-vendor-ratings" type="link" name="Vendor Ratings" onClick={() => this.props.navigate('/vendor-ratings')} />
                    <p className="dsl-m14 mb-0">
                        <Icon name="fas fa-chevron-right mx-2" size={12} color="#969faa" />
                        By Company
                    </p>
                </div>
                <div className="vendor-company-search">
                    <p className="dsl-b22 bold">Search for a Vendor</p>
                    <div className="search-small-width">
                        <AutoComplete placeholder="Search here..." className="w-100" type="vendor" options={vendors} onSearch={this.handleSearchVendor} />
                    </div>
                </div>
                <Row className="d-flex ssm-column mx-0 mt-2 ai-start">
                    <Col xs={12} md={2} className="p-0 pe-md-2">
                        <div className="vendor-company-alpha-list w-100 box-detail vendor-company-filter" ref={this.vendorAlphaListRef} onScroll={this.handleVendorAlphaListScroll}>
                            <p className="dsl-b18 bold filter-title px-4">A-Z</p>
                            {vendorAlphaList.map((item) => (
                                <div
                                    key={item.id}
                                    className={`d-h-start py-3 cursor-pointer px-4 filter-option ${selected?.id && selected.id === item.id ? 'selected' : ''}`}
                                    onClick={this.handleClickLetter(item)}
                                >
                                    <p className="dsl-b16 bold mb-0">{item?.name === 'NO' ? '#' : item?.name}</p>
                                    <p className="dsl-m12 ms-2 mb-0">({item.count})</p>
                                </div>
                            ))}
                        </div>
                    </Col>
                    <Col xs={12} md={10} className="px-0" ref={this.listContainerRef}>
                        <div className="vendor-company-list px-0">
                            <div className="d-h-between py-2 px-4">
                                <p className="dsl-b18 bold mb-0">Featured Companies</p>
                                <p className="dsl-b14">{`${moment(dateStart).format('MMM D, YY')} - ${moment(dateEnd).format('MMM D, YY')}`}</p>
                            </div>
                            <div className="d-h-start py-2 mx-4">
                                <p className="dsl-b18 bold mb-0">{search ? search.name : selected?.name === 'NO' ? '#' : selected?.name}</p>
                                <p className="dsl-m12 ms-1 mb-0">({featured.length})</p>
                            </div>
                            {featured.length > 0 ? (
                                featured.map((vendor, index) => (
                                    <VendorItem key={vendor.id} vendor={vendor} lastItem={index === featured.length - 1} onOpen={this.handleOpenCompany(vendor)} onRate={this.handleOpenRate(vendor)} />
                                ))
                            ) : (
                                <div className="py-2 px-4">
                                    <p className="dsl-b16 mb-0">No featured companies</p>
                                </div>
                            )}
                        </div>
                        <div className="vendor-company-list mt-2 px-0">
                            <div className="py-2 px-4">
                                <p className="dsl-b18 bold mb-0">Companies</p>
                            </div>
                            <div className="d-h-start py-2 mx-4">
                                <p className="dsl-b18 bold mb-0">{search ? search.name : selected?.name === 'NO' ? '#' : selected?.name}</p>
                                <p className="dsl-m12 ms-1 mb-0">({standard.length})</p>
                            </div>
                            {standard.length > 0 ? (
                                standard.map((vendor, index) => (
                                    <VendorItem key={vendor.id} vendor={vendor} lastItem={index === standard.length - 1} onOpen={this.handleOpenCompany(vendor)} onRate={this.handleOpenRate(vendor)} />
                                ))
                            ) : (
                                <div className="py-2 px-4">
                                    <p className="dsl-b16 mb-0">No standard companies</p>
                                </div>
                            )}
                        </div>
                        {selected && vendorCompanies?.data?.length >= Number(vendorCompanies?.per_page) && (
                            <Pagination
                                current={current}
                                per={per}
                                total={Math.ceil(Number(vendorCompanies?.total) / Number(vendorCompanies?.per_page))}
                                pers={[15, 50, 'all']}
                                onPer={this.handlePer}
                                onChange={this.handlePagination}
                            />
                        )}
                    </Col>
                </Row>
            </ErrorBoundary>
        );
    }
}

ByCompany.propTypes = {
    vendorCompaniesAlphabet: PropTypes.any,
    vendorCompanies: PropTypes.shape({
        data: PropTypes.array,
    }),
    getCategories: PropTypes.func,
    getCompanies: PropTypes.func,
    getCompaniesInfo: PropTypes.func,
    openCompanyView: PropTypes.func,
};

ByCompany.defaultProps = {
    vendorCompaniesAlphabet: {},
    vendorCompanies: {
        data: [],
    },
    getCategories: () => {},
    getCompanies: () => {},
    getCompaniesInfo: () => {},
    openCompanyView: () => {},
};

const mapStateToProps = (state) => ({
    authenticated: !isEmpty(state.app.token),
    vendorCompanies: state.vendor.vendorCompanies,
    vendorCompaniesAlphabet: state.vendor.vendorCompaniesAlphabet,
});

const mapDispatchToProps = (dispatch) => ({
    getCategories: (e) => dispatch(VendorActions.getcategoriesRequest(e)),
    getCompanies: (e) => dispatch(VendorActions.getvendorcompaniesRequest(e)),
    getCompaniesInfo: (e) => dispatch(VendorActions.getallvendorcompaniesRequest(e)),
    toggleModal: (e) => dispatch(AppActions.modalRequest(e)),
    openCompanyView: (payload, route) => dispatch(CompanyActions.getbusinessRequest(payload, route)),
});

const ByCompanyWithNavigate = withNavigate(ByCompany);

export default connect(mapStateToProps, mapDispatchToProps)(ByCompanyWithNavigate);
